import type { LoadedUiModeProps, TixologiSurface } from "@punchup/analytics";
import type { Comedian } from "@punchup/data/legacy/comedian";
import type { ShowInfo } from "@punchup/data/legacy/shows";
import type { VenuePagesRow } from "@punchup/data/legacy/venue_pages";

/**
 * Hosts where we deliberately do NOT forward analytics identity into the
 * Tixologi iframe (local development, where analytics is disabled anyway).
 * Every other host — punchup.live, Vercel previews, AND hosted/custom venue
 * domains — should forward identity.
 */
export const isLocalDevHost = (hostname: string): boolean =>
  hostname === "localhost" || hostname === "127.0.0.1";

export interface PunchupIdentityPayload {
  token?: string;
  email?: string;
  fullName?: string;
  userId?: string;
  mixpanelDistinctId?: string;
  initialReferrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Reads UTM attribution to hand off to the checkout iframe: the current URL's
 * utm_* query params (a direct campaign landing) first, then mixpanel's
 * first-touch values persisted on the parent page. Mirrors the widget's own
 * URL-param UTM fallback so attribution survives the cross-origin boundary
 * instead of resetting to none inside the iframe.
 */
export const readUtmParams = (
  search: string,
  mixpanelPersistence: Record<string, unknown> = {}
): { utmSource?: string; utmMedium?: string; utmCampaign?: string } => {
  const params = new URLSearchParams(search);
  const pick = (key: string): string | undefined => {
    const fromUrl = params.get(key);
    if (fromUrl) return fromUrl;
    const fromMixpanel = mixpanelPersistence[key];
    return typeof fromMixpanel === "string" && fromMixpanel
      ? fromMixpanel
      : undefined;
  };
  return {
    utmSource: pick("utm_source"),
    utmMedium: pick("utm_medium"),
    utmCampaign: pick("utm_campaign"),
  };
};

/**
 * Builds the buyer-identity payload the checkout iframe needs to run under the
 * parent page's identity (so venue-page events and in-iframe checkout join into
 * one funnel, and checkout prefills the buyer's email/name). This is delivered
 * via postMessage — NOT in the iframe URL — so the iframe URL is identity-free
 * and the SSR page is CDN-cacheable, and so an auth token + PII never land in a
 * URL (logs, referers, caches).
 *
 * The analytics identity (user id, mixpanel id, referrer, utm attribution) is
 * forwarded on every real host, including hosted/custom venue domains; only
 * local dev is skipped
 * (analytics is disabled there). Email/name/token still forward on local dev so
 * checkout prefill works while developing. Fields are set only when present.
 * Returns null when there is nothing to send.
 */
export const buildPunchupIdentityPayload = ({
  hostname,
  token,
  email,
  name,
  userId,
  mixpanelDistinctId,
  initialReferrer,
  utmSource,
  utmMedium,
  utmCampaign,
}: {
  hostname: string;
  token?: string;
  email?: string;
  name?: string;
  userId?: string;
  mixpanelDistinctId?: string;
  initialReferrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}): PunchupIdentityPayload | null => {
  const payload: PunchupIdentityPayload = {};

  if (token) payload.token = token;
  if (email) payload.email = email;
  if (name) payload.fullName = name;

  if (!isLocalDevHost(hostname)) {
    if (userId) payload.userId = userId;
    if (mixpanelDistinctId) payload.mixpanelDistinctId = mixpanelDistinctId;
    if (initialReferrer) payload.initialReferrer = initialReferrer;
    if (utmSource) payload.utmSource = utmSource;
    if (utmMedium) payload.utmMedium = utmMedium;
    if (utmCampaign) payload.utmCampaign = utmCampaign;
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

/**
 * Builds the Mixpanel property bag for the parent-side
 * `PaymentEvents.TIXOLOGI_PURCHASE_COMPLETED` event. Property names intentionally
 * mirror the upstream `UserEvents.BUY_TICKETS_CLICKED` event (`ShowId`,
 * `VenueId`, `source`, `IsTixologi`) so the click and the purchase break down
 * and join cleanly in the same funnel. The guest auth token from the iframe is
 * deliberately NOT included (it is sensitive and not analytics-relevant).
 */
export const buildTixologiPurchaseEventProps = (
  show: ShowInfo | null,
  data: { transactionCode?: string; ticketCount?: number }
) => ({
  ShowId: show?.id,
  VenueId: show?.venueId,
  Venue: show?.venue,
  TixologiEventId: show?.tixologiEventId,
  TransactionCode: data.transactionCode,
  TicketCount: data.ticketCount || 1,
  source: "tixologi_widget",
  IsTixologi: true,
});

const SURFACE_BY_PATH: ReadonlyArray<[RegExp, TixologiSurface]> = [
  [/^\/home\/dashboard\/[^/]+\/new-ticket\/?$/, "new_ticket"],
  [/^\/home\/dashboard\/[^/]+\/?$/, "show_dashboard"],
  [/^\/marketing(\/|$)/, "marketing"],
  [/^\/create-event\/?$/, "create_event"],
];

/**
 * Low-cardinality surface for the loaded-view event, so Statsig can group on it
 * (a raw path with a show id in it cannot be a dimension).
 */
export const surfaceFromPath = (pathname: string): TixologiSurface =>
  SURFACE_BY_PATH.find(([pattern]) => pattern.test(pathname))?.[1] ?? "other";

/**
 * Builds the property bag for the parent-side `TixologiEvents.LOADED_UI_MODE`
 * event: the effective view the iframe was given (eligibility AND preference),
 * who loaded it (the manager's email rides the login response), and which venue
 * or comedian the iframe is scoped to. Temporary — retired with the
 * `tixologi_green_zone` gate.
 */
export const buildLoadedUiModeProps = ({
  effectiveGreenZone,
  eligible,
  isComedian,
  pathname,
  email,
  venuePage,
  comedian,
}: {
  effectiveGreenZone: boolean;
  eligible: boolean;
  isComedian: boolean;
  /** Pathname of the iframe URL the auth-injection pass is about to load. */
  pathname: string;
  email?: string;
  venuePage?: Pick<VenuePagesRow, "id" | "name" | "slug"> | null;
  comedian?: Pick<Comedian, "id"> | null;
}): LoadedUiModeProps => {
  const scopeProps = isComedian
    ? { ComedianId: comedian?.id }
    : {
        VenuePageId: venuePage?.id,
        VenueName: venuePage?.name,
        VenueSlug: venuePage?.slug,
      };

  return {
    Mode: effectiveGreenZone ? "new" : "classic",
    Eligible: eligible,
    Scope: isComedian ? "comedian" : "venue",
    Surface: surfaceFromPath(pathname),
    Email: email,
    ...scopeProps,
  };
};
